import React from 'react';
import PropTypes from 'prop-types';
import { useSelect } from 'downshift';

import { ChevronDownIcon, DatoIcon, GatsbyIcon, NetlifyIcon, NextIcon, ShopifyIcon, PrismicIcon, VercelIcon } from 'icons';
import { Label } from '../Label';
import { Text } from '../Text';
import { cx } from 'utility';
import * as styles from './Select.module.scss';

const propTypes = {
    className: PropTypes.string,
    description: PropTypes.string,
    items: PropTypes.array,
    label: PropTypes.string,
    onSurface: PropTypes.bool,
    required: PropTypes.bool,
};

const defaultProps = {
    className: '',
    description: '',
    items: [],
    label: '',
    onSurface: false,
    required: false,
};

function getClasses({ className, onSurface }) {
    const classes = cx(
        className,
        styles['select'],
        onSurface && styles['selectOnSurface'],
    );

    return classes || undefined;
}

function getItemClasses({ isActive }) {
    const classes = cx(
        styles['item'],
        isActive && styles['itemActive'],
    );

    return classes || undefined;
}

function Select({
    className,
    description,
    items,
    label,
    name,
    onChange,
    onSurface,
    required,
}) {
    const classes = getClasses({ className, onSurface });
    const {
        isOpen,
        selectedItem,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        highlightedIndex,
        getItemProps,
    } = useSelect({
        onSelectedItemChange: changes => {
            onChange(name, changes.selectedItem);
        },
        items,
    });

    return (
        <div className={classes}>
            {label && (
                <Label required={required} {...getLabelProps()}>{label}</Label>
            )}
            <div className={styles['field']}>
                <Text as="button" className={styles['button']} emphasis="low" name={name} size={1} type="button" {...getToggleButtonProps()}>
                    {(selectedItem === 'Dato') && (
                        <DatoIcon className={styles['itemIcon']} />
                    )}
                    {(selectedItem === 'Gatsby') && (
                        <GatsbyIcon className={styles['itemIcon']} />
                    )}
                    {(selectedItem === 'Netlify') && (
                        <NetlifyIcon className={styles['itemIcon']} />
                    )}
                    {(selectedItem === 'Next') && (
                        <NextIcon className={styles['itemIcon']} />
                    )}
                    {(selectedItem === 'Shopify') && (
                        <ShopifyIcon className={styles['itemIcon']} />
                    )}
                    {(selectedItem === 'Prismic') && (
                        <PrismicIcon className={styles['itemIcon']} />
                    )}
                    {(selectedItem === 'Vercel') && (
                        <VercelIcon className={styles['itemIcon']} />
                    )}
                    {selectedItem || 'Please select'}
                    <ChevronDownIcon className={styles['buttonIcon']} />
                </Text>
                <ul className={styles['list']} {...getMenuProps()}>
                    {isOpen && items.map((item, index) => {
                        const isActive = highlightedIndex === index;
                        const itemClasses = getItemClasses({ isActive });

                        return (
                            <Text as="li" className={itemClasses} emphasis="medium" size={1} key={`${item}${index}`} {...getItemProps({item, index})}>
                                {(item === 'Dato') && (
                                    <DatoIcon className={styles['itemIcon']} />
                                )}
                                {(item === 'Gatsby') && (
                                    <GatsbyIcon className={styles['itemIcon']} />
                                )}
                                {(item === 'Netlify') && (
                                    <NetlifyIcon className={styles['itemIcon']} />
                                )}
                                {(item === 'Next') && (
                                    <NextIcon className={styles['itemIcon']} />
                                )}
                                {(item === 'Shopify') && (
                                    <ShopifyIcon className={styles['itemIcon']} />
                                )}
                                {(item === 'Prismic') && (
                                    <PrismicIcon className={styles['itemIcon']} />
                                )}
                                {(item === 'Vercel') && (
                                    <VercelIcon className={styles['itemIcon']} />
                                )}
                                {item}
                            </Text>
                        );
                    })}
                </ul>
                <div tabIndex="0" />
            </div>
            {description && (
                <Text as="span" className={styles['description']} emphasis="low" size={0}>{description}</Text>
            )}
        </div>
    );
}

Select.propTypes = propTypes;
Select.defaultProps = defaultProps;

export { Select };
